import { Card, CardBody, CardHeader, Col, Row } from "reactstrap";

const SECTION_HEADER_STYLE = {
  backgroundColor: "#eceeee",
  cursor: "default",
};

const SECTION_BODY_STYLE = { backgroundColor: "#eceeee" };

function SkeletonBar({ width = "100%", height = 14, className = "mb-2" }) {
  return (
    <p className={`placeholder-glow ${className} mb-0`}>
      <span
        className="placeholder col-12 rounded"
        style={{ width, height, display: "inline-block", minHeight: height }}
      />
    </p>
  );
}

function SkeletonLabel() {
  return <SkeletonBar width="45%" height={12} className="mb-2" />;
}

function SkeletonInput({ tall = false } = {}) {
  return (
    <SkeletonBar
      width="100%"
      height={tall ? 38 : 34}
      className="mb-0"
    />
  );
}

function SkeletonField({ tall = false }) {
  return (
    <Col md={6} lg={4}>
      <SkeletonLabel />
      <SkeletonInput tall={tall} />
    </Col>
  );
}

function SkeletonSectionCard({ title, children }) {
  return (
    <Card className="mb-3 client-profile-skeleton-card">
      <CardHeader
        className="d-flex justify-content-between align-items-center"
        style={SECTION_HEADER_STYLE}
      >
        <h6 className="mb-0 fw-bolder">{title}</h6>
        <p className="placeholder-glow mb-0">
          <span
            className="placeholder rounded-circle"
            style={{ width: 20, height: 20, display: "inline-block" }}
          />
        </p>
      </CardHeader>
      <CardBody style={SECTION_BODY_STYLE}>{children}</CardBody>
    </Card>
  );
}

function SkeletonSaveFooter() {
  return (
    <div className="d-flex justify-content-end mt-3 pt-2 border-top border-secondary border-opacity-25">
      <p className="placeholder-glow mb-0">
        <span
          className="placeholder rounded"
          style={{ width: 72, height: 36, display: "inline-block" }}
        />
      </p>
    </div>
  );
}

function PrimaryInformationSkeleton() {
  return (
    <SkeletonSectionCard title="Primary Information">
      <Row className="gy-3 gx-4">
        <Col md={12} className="d-flex justify-content-center mb-2">
          <p className="placeholder-glow mb-0">
            <span
              className="placeholder rounded-circle"
              style={{ width: 150, height: 150, display: "inline-block" }}
            />
          </p>
        </Col>
        <Col md={6} lg={3}>
          <SkeletonLabel />
          <SkeletonInput />
        </Col>
        <Col md={6} lg={3}>
          <SkeletonLabel />
          <SkeletonInput />
        </Col>
        <Col md={6} lg={3}>
          <SkeletonLabel />
          <SkeletonInput tall />
        </Col>
        <Col md={6} lg={3}>
          <SkeletonLabel />
          <SkeletonInput />
        </Col>
      </Row>
      <SkeletonSaveFooter />
    </SkeletonSectionCard>
  );
}

function CompanyInformationSkeleton() {
  return (
    <SkeletonSectionCard title="Company Information">
      <Row className="gy-3 gx-4">
        {Array.from({ length: 9 }).map((_, i) => (
          <SkeletonField key={i} tall={i === 8} />
        ))}
        <Col md={12}>
          <SkeletonLabel />
          <SkeletonInput tall />
        </Col>
      </Row>
      <SkeletonSaveFooter />
    </SkeletonSectionCard>
  );
}

function FinanceBillingSkeleton() {
  return (
    <SkeletonSectionCard title="Finance & Billing">
      <Row className="gy-3 gx-4 mb-2">
        <Col xs={12}>
          <SkeletonBar width="40%" height={32} />
        </Col>
      </Row>
      <Row className="gy-3 gx-4">
        <Col md={6}>
          <SkeletonLabel />
          <SkeletonInput />
        </Col>
        <Col md={6}>
          <SkeletonLabel />
          <SkeletonInput />
        </Col>
        <Col md={6}>
          <SkeletonLabel />
          <SkeletonInput tall />
        </Col>
        <Col md={12}>
          <SkeletonLabel />
          <SkeletonInput tall />
        </Col>
      </Row>
      <SkeletonSaveFooter />
    </SkeletonSectionCard>
  );
}

function HealthSafetySkeleton() {
  return (
    <SkeletonSectionCard title="Health & Safety Contact">
      <Row className="gy-3 gx-4">
        <Col md={6}>
          <SkeletonLabel />
          <SkeletonInput />
        </Col>
        <Col md={6}>
          <SkeletonLabel />
          <SkeletonInput />
        </Col>
        <Col md={6}>
          <SkeletonLabel />
          <SkeletonInput tall />
        </Col>
      </Row>
      <SkeletonSaveFooter />
    </SkeletonSectionCard>
  );
}

function OwnershipControlSkeleton() {
  return (
    <SkeletonSectionCard title="Ownership & Control Declaration">
      <Row className="gy-3 gx-4">
        <Col md={12}>
          <div className="d-flex align-items-center gap-2 mb-2">
            <p className="placeholder-glow mb-0">
              <span
                className="placeholder rounded"
                style={{ width: 18, height: 18, display: "inline-block" }}
              />
            </p>
            <SkeletonBar width="55%" height={14} />
          </div>
        </Col>
        <Col md={12}>
          <SkeletonBar width="30%" height={16} className="mb-3" />
        </Col>
        {[0, 1].map((row) => (
          <Col md={12} key={`dir-${row}`}>
            <Row className="gy-3 gx-4 align-items-end">
              <Col md={6}>
                <SkeletonLabel />
                <SkeletonInput />
              </Col>
              <Col md={6}>
                <SkeletonLabel />
                <SkeletonInput />
              </Col>
              <Col md={6}>
                <SkeletonLabel />
                <SkeletonBar width="100%" height={38} />
              </Col>
              <Col md={6}>
                <SkeletonLabel />
                <SkeletonBar width="100%" height={38} />
              </Col>
            </Row>
            <hr className="my-3 border-secondary border-opacity-25" />
          </Col>
        ))}
        <Col md={12}>
          <SkeletonBar width="35%" height={16} className="mb-2" />
          <div className="d-flex gap-4 mb-3">
            <SkeletonBar width={120} height={18} />
            <SkeletonBar width={100} height={18} />
          </div>
        </Col>
        <Col md={12}>
          <Row className="gy-3 gx-4">
            <Col md={6}>
              <SkeletonLabel />
              <SkeletonInput />
            </Col>
            <Col md={6}>
              <SkeletonLabel />
              <SkeletonInput />
            </Col>
            <Col md={6}>
              <SkeletonLabel />
              <SkeletonBar width="100%" height={38} />
            </Col>
            <Col md={6}>
              <SkeletonLabel />
              <SkeletonBar width="100%" height={38} />
            </Col>
          </Row>
        </Col>
      </Row>
      <SkeletonSaveFooter />
    </SkeletonSectionCard>
  );
}

function InsuranceSkeleton() {
  return (
    <SkeletonSectionCard title="Insurance">
      <Row className="gy-3 gx-4">
        <Col md={6}>
          <div className="d-flex align-items-center gap-2 mb-2">
            <SkeletonBar width="50%" height={14} />
            <p className="placeholder-glow mb-0">
              <span
                className="placeholder rounded-pill"
                style={{ width: 72, height: 22, display: "inline-block" }}
              />
            </p>
          </div>
          <SkeletonLabel />
          <SkeletonBar width="100%" height={38} />
        </Col>
        <Col md={6}>
          <div className="d-flex align-items-center gap-2 mb-2">
            <SkeletonBar width="45%" height={14} />
            <p className="placeholder-glow mb-0">
              <span
                className="placeholder rounded-pill"
                style={{ width: 72, height: 22, display: "inline-block" }}
              />
            </p>
          </div>
          <SkeletonLabel />
          <SkeletonBar width="100%" height={38} />
        </Col>
        <Col md={12}>
          <div className="d-flex align-items-center gap-2">
            <p className="placeholder-glow mb-0">
              <span
                className="placeholder rounded"
                style={{ width: 18, height: 18, display: "inline-block" }}
              />
            </p>
            <SkeletonBar width="70%" height={14} />
          </div>
        </Col>
      </Row>
      <SkeletonSaveFooter />
    </SkeletonSectionCard>
  );
}

export default function ClientProfileSkeleton() {
  return (
    <div className="client-profile-skeleton" aria-busy="true" aria-label="Loading profile">
      <p className="placeholder-glow mb-3">
        <span className="placeholder col-12 rounded" style={{ height: 52 }} />
      </p>
      <PrimaryInformationSkeleton />
      <CompanyInformationSkeleton />
      <FinanceBillingSkeleton />
      <HealthSafetySkeleton />
      <OwnershipControlSkeleton />
      <InsuranceSkeleton />
    </div>
  );
}
